import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Star } from 'lucide-react';
import { countriesAPI } from '@/lib/api';

const CountriesPage = () => {
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await countriesAPI.getAll();
        setCountries(response.data);
        setFilteredCountries(response.data);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
        setCountries([]);
        setFilteredCountries([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const filtered = countries.filter((country) =>
      country.name_fa.includes(searchTerm) || country.name_en.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCountries(filtered);
  }, [searchTerm, countries]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen py-12" data-testid="countries-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light text-white mb-4" data-testid="page-title">کشورهای جهان</h1>
          <p className="text-zinc-400">اطلاعات کامل {countries.length} کشور برتر جهان</p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
            <input
              type="text"
              placeholder="جستجوی کشور..."
              data-testid="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-full px-12 py-4 text-white placeholder:text-zinc-500 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            />
          </div>
        </div>

        {/* Countries Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredCountries.map((country) => (
            <Link
              key={country.id}
              to={`/countries/${country.id}`}
              data-testid="country-card"
              className="group relative overflow-hidden rounded-2xl aspect-[3/4] glass-effect border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-1 gold-glow-hover"
            >
              {country.image_url && (
                <img
                  src={country.image_url}
                  alt={country.name_fa}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="text-4xl mb-2">{country.flag_emoji}</div>
                <h3 className="text-2xl font-medium text-white mb-1">{country.name_fa}</h3>
                <p className="text-sm text-zinc-300 mb-3">{country.capital_fa}</p>
                <div className="flex items-center gap-4 text-xs text-zinc-400">
                  <div className="flex items-center gap-1">
                    <Star size={14} className="text-[#D4AF37]" fill="#D4AF37" />
                    <span>{country.quality_of_life_score}</span>
                  </div>
                  <span>امنیت: {country.security_score}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredCountries.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-400">کشوری با این نام یافت نشد</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CountriesPage;
